import React, {useCallback, useEffect, useRef, useState} from 'react';
import {InlineSvgIcon} from './InlineSvgIcon';
import styles from './LawyerProjectMessageComposer.module.css';
import {useTranslation} from '../i18n/I18nContext';

const MIN_TEXTAREA_ROWS = 2;
const MAX_TEXTAREA_ROWS = 15;
const LINE_HEIGHT_PX = 24;

const COMPOSER_MOD_LABEL = /Mac|iPhone|iPod|iPad/i.test(
  typeof navigator !== 'undefined' ? (navigator.platform || navigator.userAgent) : '',
) ? '⌘' : 'Ctrl';

const SHOW_COMPOSER_SEND_HINT = typeof window !== 'undefined'
  && window.matchMedia
  && window.matchMedia('(hover: hover) and (pointer: fine)').matches;

/**
 * @param {File[]} next
 * @param {File[]} prev
 */
function mergeUniqueFiles(prev, next) {
  const out = [...prev];
  const sig = (f) => `${f.name}-${f.size}-${f.lastModified}`;
  const seen = new Set(out.map(sig));
  next.forEach((f) => {
    const s = sig(f);
    if (!seen.has(s)) {
      seen.add(s);
      out.push(f);
    }
  });
  return out;
}

/**
 * CRM project reply composer (always visible, in-flow below thread header).
 * @param {{ onSend?: (text: string, files: File[]) => Promise<void>; placeholder?: string; initialDraft?: string; onDraftChange?: (text: string) => void }} props
 */
export default function LawyerProjectMessageComposer({
  onSend,
  placeholder,
  initialDraft = '',
  onDraftChange,
}) {
  const {t} = useTranslation();
  const resolvedPlaceholder = placeholder || t('project.thread.writePlaceholder');
  const [draft, setDraft] = useState(initialDraft);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState(null);
  const [focused, setFocused] = useState(false);
  const messageFileInputRef = useRef(null);
  const textareaRef = useRef(null);
  const wrapRef = useRef(null);

  const isComposerActionTarget = (target) => {
    if (!(target instanceof Element)) {
      return false;
    }
    return Boolean(
      target.closest('.lawyer-project-composer__file-link')
      || target.closest('.lawyer-project-composer__icon-btn')
      || target.closest('.lawyer-project-composer__send-btn'),
    );
  };

  const triggerFocusPulse = useCallback(() => {
    const el = wrapRef.current;
    if (!el) {
      return;
    }
    el.classList.remove(styles.composerInputWrapPulse);
    void el.offsetWidth;
    el.classList.add(styles.composerInputWrapPulse);
  }, []);

  const handleWrapAnimationEnd = (e) => {
    if (e.target !== wrapRef.current) {
      return;
    }
    wrapRef.current?.classList.remove(styles.composerInputWrapPulse);
  };

  const handleComposerWrapMouseDown = (e) => {
    if (isComposerActionTarget(e.target) || sending) {
      return;
    }

    const target = e.target;
    if (target instanceof Element) {
      if (target.closest('textarea')) {
        return;
      }
      if (target.closest('button')) {
        return;
      }
    }

    e.preventDefault();
    const wasFocused = document.activeElement === textareaRef.current;
    textareaRef.current?.focus();
    if (wasFocused) {
      triggerFocusPulse();
    }
  };

  const handleTextareaFocus = () => {
    setFocused(true);
    triggerFocusPulse();
  };

  const handleTextareaBlur = () => {
    setFocused(false);
  };

  const minHeightPx = MIN_TEXTAREA_ROWS * LINE_HEIGHT_PX;
  const maxHeightPx = MAX_TEXTAREA_ROWS * LINE_HEIGHT_PX;

  const adjustTextareaHeight = useCallback(() => {
    const el = textareaRef.current;
    if (!el) {
      return;
    }
    el.style.height = 'auto';
    const next = Math.min(Math.max(el.scrollHeight, minHeightPx), maxHeightPx);
    el.style.height = `${next}px`;
  }, [minHeightPx, maxHeightPx]);

  useEffect(() => {
    setDraft(initialDraft);
  }, [initialDraft]);

  useEffect(() => {
    adjustTextareaHeight();
  }, [draft, adjustTextareaHeight]);

  const openMessageFilePicker = () => {
    messageFileInputRef.current?.click();
  };

  const handleFileInputChange = (e) => {
    const picked = Array.from(e.target?.files || []).filter(Boolean);
    if (picked.length) {
      setPendingFiles((prev) => mergeUniqueFiles(prev, picked));
    }
    if (e.target) {
      e.target.value = '';
    }
  };

  const removePendingFile = (index) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const canSend = draft.trim().length > 0 || pendingFiles.length > 0;

  const handleSend = async () => {
    const text = draft.trim();
    if ((!text && pendingFiles.length === 0) || !onSend) {
      return;
    }
    setSending(true);
    setSendError(null);
    try {
      await onSend(text, pendingFiles);
      setDraft('');
      setPendingFiles([]);
    } catch (e) {
      setSendError(e?.message || t('project.thread.sendFailed'));
    } finally {
      setSending(false);
    }
  };

  const handleTextareaKeyDown = (e) => {
    if (e.key !== 'Enter' || (!e.metaKey && !e.ctrlKey)) {
      return;
    }
    if (sending || !canSend) {
      e.preventDefault();
      return;
    }
    e.preventDefault();
    void handleSend();
  };

  return (
    <div className={`${styles.composerRoot} lawyer-project-composer__root`}>
      <input
        ref={messageFileInputRef}
        type="file"
        className={styles.hiddenInput}
        multiple
        aria-hidden="true"
        tabIndex={-1}
        onChange={handleFileInputChange}
      />
      <div
        ref={wrapRef}
        className={styles.composerInputWrap}
        onMouseDown={handleComposerWrapMouseDown}
        onAnimationEnd={handleWrapAnimationEnd}
      >
        <textarea
          ref={textareaRef}
          className={`${styles.composerTextarea} lawyer-project-composer__textarea`}
          placeholder={resolvedPlaceholder}
          value={draft}
          onChange={(e) => {
            const next = e.target.value;
            setDraft(next);
            onDraftChange?.(next);
          }}
          onInput={adjustTextareaHeight}
          onFocus={handleTextareaFocus}
          onBlur={handleTextareaBlur}
          onKeyDown={handleTextareaKeyDown}
          rows={MIN_TEXTAREA_ROWS}
          aria-label={resolvedPlaceholder}
          disabled={sending}
        />
        {pendingFiles.length > 0 ? (
          <ul className={styles.pendingFileList} aria-label={t('project.thread.attachedFiles')}>
            {pendingFiles.map((f, idx) => (
              <li key={`${f.name}-${f.size}-${f.lastModified}-${idx}`} className={styles.pendingFileItem}>
                <span className={styles.pendingFileName}>{f.name}</span>
                <button
                  type="button"
                  className={styles.removeFileBtn}
                  onClick={() => removePendingFile(idx)}
                  aria-label={`${t('project.thread.removeFile')} ${f.name}`}
                  disabled={sending}
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        ) : null}
        <div className={styles.composerFooter}>
          {SHOW_COMPOSER_SEND_HINT ? (
            <span
              className={`${styles.composerHint}${focused ? ` ${styles.composerHintVisible}` : ''}`}
              aria-hidden="true"
            >
              {t('project.thread.sendShortcutHint', { mod: COMPOSER_MOD_LABEL })}
            </span>
          ) : null}
          <div className={styles.composerActions}>
            <button
              type="button"
              className={`${styles.fileLinkBtn} lawyer-project-composer__file-link`}
              onClick={openMessageFilePicker}
              disabled={sending}
            >
              {t('project.thread.shareFiles')}
            </button>
            <button
              type="button"
              className={`${styles.composerIconBtn} lawyer-project-composer__icon-btn`}
              onClick={openMessageFilePicker}
              aria-label={t('project.thread.shareFiles')}
              disabled={sending}
            >
              <InlineSvgIcon
                width={20}
                className={`${styles.composerFileIcon} lawyer-project-composer__file-icon-svg`}
                src="/svg/icon/file.svg"
                aria-hidden="true"
              />
            </button>
            <button
              type="button"
              className={`${styles.composerSendBtn} lawyer-project-composer__send-btn`}
              aria-label={t('project.thread.send')}
              onClick={handleSend}
              disabled={sending || !canSend}
            >
              <i className="fa fa-chevron-right" aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>
      {sendError ? (
        <p className={styles.sendError}>{sendError}</p>
      ) : null}
    </div>
  );
}
